import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Square, X, BookOpen, Cog, Info, LogOut, User as UserIcon, Wallet, Crown, Edit3, Check, Sparkles, Loader2, ArrowLeft, ClipboardEdit } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { usePW } from '@/lib/store';
import { useAuth } from '@/lib/auth_context';
import { useWordcount } from '@/hooks/useWordcount';
import LeafBadge from '@/components/badges/LeafBadge';
import StudioHealthPill from '@/components/StudioHealthPill';
import LanguageCapsule from '@/components/LanguageCapsule';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Tier pill colours — each subscription level gets a distinct accent.
const TIER_STYLES = {
  free:         { bg: 'bg-neutral-200',  text: 'text-neutral-700', label: 'Free' },
  trial:        { bg: 'bg-blue-100',     text: 'text-blue-700',    label: 'Trial' },
  basic:        { bg: 'bg-slate-200',    text: 'text-slate-700',   label: 'Starter' },
  pro:          { bg: 'bg-indigo-100',   text: 'text-indigo-700',  label: 'Pro' },
  pro_plus:     { bg: 'bg-violet-100',   text: 'text-violet-700',  label: 'Pro+' },
  // Named tiers — v4 jewel tones
  scribe:       { bg: 'bg-[#0284C7]',    text: 'text-white',       label: 'Scribe' },
  author:       { bg: 'bg-[#E11D48]',    text: 'text-white',       label: 'Author' },
  publisher:    { bg: 'bg-[#D97706]',    text: 'text-white',       label: 'Publisher' },
  studio:       { style: { background: 'linear-gradient(90deg,#7C3AED,#D946EF)', color: '#fff' }, label: 'Studio' },
  // Legacy plan-id aliases
  premier:      { bg: 'bg-[#E11D48]',    text: 'text-white',       label: 'Author' },
  studio_byok:  { style: { background: 'linear-gradient(90deg,#7C3AED,#D946EF)', color: '#fff' }, label: 'Studio BYOK' },
};
function TierPill({ tier, isGodMode }) {
  if (!tier && !isGodMode) return null;
  const s = TIER_STYLES[tier] || { bg: 'bg-neutral-200', text: 'text-neutral-700', label: tier };
  const label = isGodMode ? (s.label + ' ★') : s.label;
  return (
    <span
      data-testid="titlebar-tier-pill"
      className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold tracking-wider ${s.bg || ''} ${s.text || ''}`}
      style={s.style}
      title={isGodMode ? 'Admin God Mode — full Studio access' : `Your current plan: ${s.label}`}
    >
      {isGodMode && <Crown className="h-2.5 w-2.5" />}
      {label}
    </span>
  );
}

export function TitleBar({ onAbout, onSettings, onBuyTokens }) {
  const { project, updateProject, streamingChapterId, building } = usePW();
  const { user, logout } = useAuth();
  const wc = useWordcount();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ── Global AI working indicator ──────────────────────────────────────────
  // Counts in-flight AI API calls (via axios interceptor events) + SSE streams.
  const [aiCallCount, setAiCallCount] = useState(0);
  useEffect(() => {
    const onStart = () => setAiCallCount((n) => n + 1);
    const onEnd   = () => setAiCallCount((n) => Math.max(0, n - 1));
    window.addEventListener('loom:ai-start', onStart);
    window.addEventListener('loom:ai-end',   onEnd);
    return () => {
      window.removeEventListener('loom:ai-start', onStart);
      window.removeEventListener('loom:ai-end',   onEnd);
    };
  }, []);
  const isAiWorking = aiCallCount > 0 || !!streamingChapterId || !!building;

  // ── "Editor Review In Progress" badge ────────────────────────────────────
  // Polls the backend once per project load; never blocks rendering.
  const [editorReview, setEditorReview] = useState(null); // {id, status, stage} | null
  useEffect(() => {
    if (!project?.id) { setEditorReview(null); return; }
    let cancelled = false;
    axios.get(`${API}/editing/requests/for-project/${project.id}`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setEditorReview(data.request || null); })
      .catch(() => { if (!cancelled) setEditorReview(null); });
    return () => { cancelled = true; };
  }, [project?.id]);

  // ── Inline manuscript rename (click on title bar) ──────────────────────────
  // File menu → "Rename Manuscript…" now opens the RenameManuscriptDialog
  // modal (wired in AppShell). This inline handler is kept for the direct
  // click-on-title interaction inside the TitleBar itself.
  const [renaming, setRenaming]   = useState(false);
  const [draft, setDraft]         = useState('');
  const [saving, setSaving]       = useState(false);
  const inputRef      = useRef(null);
  const cancellingRef = useRef(false); // guard: prevents blur from committing when cancel is pressed

  // NOTE: loom:open-manuscript-rename is handled by AppShell → RenameManuscriptDialog.
  // The inline rename below is only for direct title-bar clicks.

  useEffect(() => {
    if (renaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renaming]);

  const startRename = useCallback(() => {
    if (!project) return;
    setDraft(project.title || project.name || '');
    setRenaming(true);
  }, [project]);

  const cancelRename = useCallback(() => {
    cancellingRef.current = false;
    setRenaming(false);
    setDraft('');
  }, []);

  const commitRename = useCallback(async () => {
    if (cancellingRef.current) return; // blur was from cancel click — skip save
    const next = (draft || '').trim().slice(0, 200);
    const current = project?.title || project?.name || '';
    if (!next || next === current) { cancelRename(); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/projects/${project.id}/global-rename`,
        { title: next },
        { withCredentials: true },
      );
      if (updateProject) await updateProject({ title: next, name: next });
      toast.success(`Renamed to "${next}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rename failed.');
    } finally {
      setSaving(false);
      setRenaming(false);
      setDraft('');
    }
  }, [draft, project, updateProject, cancelRename]);

  const initials = (user?.name || user?.email || '?')
    .split(/\s+/).map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  const projectLabel = project?.title || project?.name || t('appShell.loading');

  return (
    <div className="titlebar-bg flex h-9 items-center justify-between border-b border-chrome-border px-3 text-[12px] text-chrome-foreground">
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex h-5 w-5 items-center justify-center rounded-sm bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-inset-top">
          <BookOpen className="h-3 w-3" strokeWidth={2.5} />
        </div>
        <span className="font-semibold tracking-tight">The Loom</span>
        <span className="rounded-sm bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold tracking-wider text-primary" data-testid="pw-version-badge">V2.38_f55</span>
        {/* Tier pill — shows active subscription next to the version badge */}
        <TierPill tier={user?.subscription_tier} isGodMode={user?.is_god_mode} />
        {/* Global AI-thinking badge — always visible when any LLM call is running */}
        {isAiWorking && (
          <span
            data-testid="titlebar-ai-thinking"
            className="inline-flex items-center gap-1.5 rounded-sm border border-amber-400/40 bg-amber-500/20 px-2 py-0.5 text-[11px] font-semibold tracking-wide text-amber-700 dark:text-amber-300 shadow-sm"
            title="AI is working…"
          >
            <Loader2 className="h-3 w-3 animate-spin" />
            AI working…
          </span>
        )}
        <span className="text-muted-foreground">—</span>
        {/* ── Back to projects button — navigates to Manuscript Library ── */}
        <button
          type="button"
          onClick={() => navigate('/stacks')}
          title="Back to manuscript library"
          data-testid="titlebar-back-to-projects"
          className="flex items-center gap-0.5 rounded px-1 py-0.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          <span className="text-[11px]">Projects</span>
        </button>
        <span className="text-muted-foreground/50">/</span>
        {/* ── Inline manuscript title with click-to-rename ── */}
        {project && renaming ? (
          <div className="flex items-center gap-1" data-testid="titlebar-rename-form">
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
              }}
              onBlur={commitRename}
              maxLength={200}
              className="h-6 min-w-[140px] max-w-[260px] rounded border border-primary/60 bg-card px-1.5 text-[12px] font-medium text-foreground outline-none focus:ring-1 focus:ring-primary/40"
              data-testid="titlebar-rename-input"
            />
            <button
              type="button"
              onClick={commitRename}
              disabled={saving}
              className="flex h-5 w-5 items-center justify-center rounded text-emerald-600 hover:bg-emerald-50 transition-colors"
              title="Save title"
              data-testid="titlebar-rename-confirm"
            >
              <Check className="h-3 w-3" />
            </button>
            <button
              type="button"
              onMouseDown={() => { cancellingRef.current = true; }}
              onClick={cancelRename}
              className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-secondary transition-colors"
              title="Cancel"
              data-testid="titlebar-rename-cancel"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={project ? startRename : undefined}
            className={
              'group flex items-center gap-1 rounded px-1 py-0.5 text-muted-foreground transition-colors '
              + (project ? 'hover:bg-secondary hover:text-foreground cursor-pointer' : 'cursor-default')
            }
            title={project ? 'Click to rename manuscript' : undefined}
            data-testid="titlebar-project-name"
          >
            <span className="truncate max-w-[220px]">{projectLabel}</span>
            {project && (
              <Edit3
                className="h-2.5 w-2.5 shrink-0 opacity-0 group-hover:opacity-70 transition-opacity"
                data-testid="titlebar-rename-icon"
              />
            )}
          </button>
        )}
        {project?.folder && (
          <span className="text-foreground/80">· {project.folder}</span>
        )}
        {/* "Editor Review In Progress" badge — shown when an active editing
            request exists for this project. Clicking navigates to the workspace. */}
        {editorReview && (
          <button
            type="button"
            title="Editor review in progress — click to view markups"
            onClick={() => navigate(`/editor/review/${editorReview.id}`)}
            data-testid="titlebar-editor-review-badge"
            className="ml-2 flex items-center gap-1 rounded-full border border-amber-400/60 bg-amber-50 px-2 py-0.5 text-[10.5px] font-semibold text-amber-700 hover:bg-amber-100 transition-colors dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-600/40"
          >
            <ClipboardEdit className="h-2.5 w-2.5 shrink-0" />
            Editor review in progress
          </button>
        )}
      </div>

      <div className="flex items-center">
        {/* iter-p2-c — Premier-only weighted studio-health pill. */}
        <div className="mr-2 hidden md:block">
          <StudioHealthPill />
        </div>
        {/* Feb-2026 · i18n — compact language selector in the top-nav */}
        <div className="mr-1 hidden md:block">
          <LanguageCapsule variant="compact" />
        </div>
        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="mr-2 flex items-center gap-2 rounded-sm px-1.5 py-1 hover:bg-secondary">
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-5 w-5 rounded-full" />
              ) : (
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/80 text-[9px] font-semibold text-primary-foreground">{initials}</div>
              )}
              <span className="hidden md:inline max-w-[160px] truncate text-[11.5px]">{user?.name || user?.email}</span>
              {wc.current_badge && (
                <LeafBadge tier={wc.current_badge} size={13}
                             title={`${(wc.total_words || 0).toLocaleString()} lifetime words · ${wc.current_badge}`} />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[220px] text-[12.5px]">
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('appShell.signedInAs')}</DropdownMenuLabel>
            <div className="px-2 pb-2 text-[12px]">
              <div className="font-medium">{user?.name}</div>
              <div className="truncate text-[11px] text-muted-foreground">{user?.email}</div>
              <div className="mt-1 font-mono-stat text-[10.5px] text-muted-foreground">{t('appShell.tokensPurchased', { count: (user?.tokens_purchased || 0).toLocaleString() })}</div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onBuyTokens} className="gap-2"><Wallet className="h-3.5 w-3.5" /> {t('appShell.buyTokens')}</DropdownMenuItem>
            <DropdownMenuItem onClick={onSettings} className="gap-2"><Cog className="h-3.5 w-3.5" /> {t('appShell.accountSettings')}</DropdownMenuItem>
            <DropdownMenuItem onClick={onAbout} className="gap-2"><Info className="h-3.5 w-3.5" /> {t('appShell.aboutLoom')}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="gap-2 text-destructive"><LogOut className="h-3.5 w-3.5" /> {t('appShell.signOut')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <button onClick={onAbout} title={t('appShell.about')} className="flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
          <Info className="h-3.5 w-3.5" />
        </button>
        <button onClick={onSettings} title={t('appShell.settings')} className="flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
          <Cog className="h-3.5 w-3.5" />
        </button>
        {/* Theme toggle — one click, dark/light */}
        <ThemeToggle className="mr-1" showLabel />
        {/* ── Ask AI — opens Writing Coach card ── */}
        <button
          data-testid="titlebar-ask-ai-btn"
          title="Ask AI Writing Coach"
          onClick={() => window.dispatchEvent(new CustomEvent('loom:open-coach-card'))}
          className="flex h-9 w-9 items-center justify-center text-muted-foreground transition-colors hover:bg-secondary hover:text-amber-400"
        >
          <Sparkles className="h-3.5 w-3.5" />
        </button>
        <div className="mx-1 h-4 w-px bg-chrome-border" />
        <button className="flex h-9 w-11 items-center justify-center text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
          <Minus className="h-3.5 w-3.5" />
        </button>
        <button className="flex h-9 w-11 items-center justify-center text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
          <Square className="h-3 w-3" />
        </button>
        <button className="flex h-9 w-11 items-center justify-center text-muted-foreground transition-colors hover:bg-destructive hover:text-destructive-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
